import Button from '@material-ui/core/Button';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import currencyFormatter from '@sprinx/formatting/currencyFormatter';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { currencyState } from '../../api/appState';
import { shoppingCartState } from '../../api/shoppingCart';
import useRouteUrlFactory from '../../hooks/useRouteUrlFactory';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';
import CartBuyForFreeDelivery from '../../pages/CartSummaryPage/CartBuyForFreeDelivery';
import CartShipmentInfo from '../CartShipmentInfo';

export type ShoppingCartAsideTotalsProps = StandardDivProps<ShoppingCartAsideTotalsClassKey>;

export type ShoppingCartAsideTotalsClassKey =
  | 'root'
  | 'shipment'
  | 'prices'
  | 'priceRow'
  | 'priceLabel'
  | 'priceValue'
  | 'price'
  | 'priceWithTax'
  | 'actions'
  | 'orderButton'
  | 'deliveryFree';

const themeSettings = { name: 'ShoppingCartAsideTotals' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ShoppingCartAsideTotalsClassKey, {}>({
      root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderTop: `2px solid ${theme.palette.divider}`,
      },
      shipment: {
        alignSelf: 'stretch',
        marginTop: theme.spacing(1),
      },
      prices: {
        alignSelf: 'stretch',
        padding: theme.spacing(1.5, 0.5, 0),
      },
      priceRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
      },
      priceLabel: {
        marginRight: theme.spacing(1),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
      },
      priceValue: {
        marginLeft: 'auto',
        whiteSpace: 'nowrap',
      },
      price: {
        fontSize: '1.35rem',
        fontWeight: theme.typography.fontWeightBold,
        lineHeight: 1.3,
      },
      priceWithTax: {
        marginTop: theme.spacing(0.5),
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
      },
      actions: {
        display: 'flex',
        flexDirection: 'column',
        '& button': {
          marginTop: theme.spacing(2),
        },
      },
      orderButton: {
        marginTop: theme.spacing(1.5),
        backgroundColor: '#002969',
        color: 'white',
        '&:hover': {
          backgroundColor: '#003485',
        },
      },
      deliveryFree: {
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
      },
    }),
  themeSettings,
);

const ShoppingCartAsideTotals = React.forwardRef<HTMLDivElement, ShoppingCartAsideTotalsProps>(
  ({ className, classes: pClasses }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const currency = useRecoilValue(currencyState);
    const cart = useRecoilValue(shoppingCartState);
    const routeUrlFactory = useRouteUrlFactory();
    const t = useTranslateWithFallback();

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        <CartShipmentInfo className={classes.shipment} stacked />
        <div className={classes.prices}>
          <div className={classes.priceRow}>
            <span className={classes.priceLabel}>{t('common/totalWithoutTax')}</span>
            <span className={clsx(classes.priceValue, classes.price)}>
              {currencyFormatter(cart?.cartContent.totalSubjects || 0, currency)}
            </span>
          </div>
          {cart?.cartContent.total != cart?.cartContent.totalWithTax && (
            <div className={clsx(classes.priceRow, classes.priceWithTax)}>
              <span className={classes.priceLabel}>{t('common/totalWithTax')}</span>
              <span className={classes.priceValue}>
                {currencyFormatter(cart?.cartContent.totalSubjectsWithTax || 0, currency)}
              </span>
            </div>
          )}
        </div>
        <div className={classes.actions}>
          <Button
            component={RouterLink}
            to={routeUrlFactory('cartSummary')}
            className={classes.orderButton}
            size='large'
            variant='contained'
          >
            {t('shoppingCart/order')}
          </Button>
          <CartBuyForFreeDelivery className={classes.deliveryFree} />
        </div>
      </div>
    );
  },
);

ShoppingCartAsideTotals.displayName = 'ShoppingCartAsideTotals';

export default withThemeProps(themeSettings)(ShoppingCartAsideTotals);
